import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  FaTrash, FaSearch, FaEnvelope, FaEnvelopeOpen,
  FaPhone, FaCalendar, FaEye, FaTimes
} from 'react-icons/fa';
import { contactsApi } from '../../services/api';
import { AdminLayout } from '../../components/admin';

const ContactsManagement = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, unread: 0, read: 0, today: 0 });
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });
  const [filters, setFilters] = useState({ isRead: '', search: '' });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [viewContact, setViewContact] = useState(null);

  const fetchContacts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await contactsApi.getAll(
        pagination.page,
        10,
        filters.isRead,
        filters.search
      );
      if (response.success) {
        setContacts(response.data || []);
        setPagination(prev => ({
          ...prev,
          totalPages: response.pagination?.total_pages || 1
        }));
      }
    } catch (err) {
      console.error('Failed to fetch contacts:', err);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, filters.isRead, filters.search]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await contactsApi.getStats();
      if (response.success) {
        setStats(response.data.stats);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, []);

  useEffect(() => {
    fetchContacts();
    fetchStats();
  }, [fetchContacts, fetchStats]);

  const handleDelete = async (id) => {
    try {
      const response = await contactsApi.delete(id);
      if (response.success) {
        setDeleteConfirm(null);
        setViewContact(null);
        fetchContacts();
        fetchStats();
      }
    } catch (err) {
      console.error('Failed to delete contact:', err);
    }
  };

  const handleMarkRead = async (id, isRead) => {
    try {
      const response = isRead
        ? await contactsApi.markRead(id)
        : await contactsApi.markUnread(id);
      if (response.success) {
        fetchContacts();
        fetchStats();
        if (viewContact && viewContact.id === id) {
          setViewContact({ ...viewContact, is_read: isRead });
        }
      }
    } catch (err) {
      console.error('Failed to update contact:', err);
    }
  };

  const openViewModal = async (contact) => {
    setViewContact(contact);
    if (!contact.is_read) {
      await handleMarkRead(contact.id, true);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-dark-800">Contact Messages</h1>
          <p className="text-dark-500 mt-1">View and manage contact form submissions</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <motion.div
            className="bg-white rounded-xl shadow-sm p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-dark-500 text-sm">Total</p>
                <p className="text-2xl font-bold text-dark-800">{stats.total}</p>
              </div>
              <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                <FaEnvelope className="w-5 h-5 text-primary-500" />
              </div>
            </div>
          </motion.div>

          <motion.div
            className="bg-white rounded-xl shadow-sm p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-dark-500 text-sm">Unread</p>
                <p className="text-2xl font-bold text-red-600">{stats.unread}</p>
              </div>
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <FaEnvelope className="w-5 h-5 text-red-500" />
              </div>
            </div>
          </motion.div>

          <motion.div
            className="bg-white rounded-xl shadow-sm p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-dark-500 text-sm">Read</p>
                <p className="text-2xl font-bold text-green-600">{stats.read}</p>
              </div>
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <FaEnvelopeOpen className="w-5 h-5 text-green-500" />
              </div>
            </div>
          </motion.div>

          <motion.div
            className="bg-white rounded-xl shadow-sm p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-dark-500 text-sm">Today</p>
                <p className="text-2xl font-bold text-blue-600">{stats.today}</p>
              </div>
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <FaCalendar className="w-5 h-5 text-blue-500" />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-dark-400" />
            <input
              type="text"
              placeholder="Search messages..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setFilters({ ...filters, isRead: '' })}
              className={`px-4 py-2 rounded-lg transition-colors ${
                filters.isRead === ''
                  ? 'bg-primary-500 text-white'
                  : 'bg-white text-dark-600 hover:bg-gray-100'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilters({ ...filters, isRead: '0' })}
              className={`px-4 py-2 rounded-lg transition-colors ${
                filters.isRead === '0'
                  ? 'bg-primary-500 text-white'
                  : 'bg-white text-dark-600 hover:bg-gray-100'
              }`}
            >
              Unread
            </button>
            <button
              onClick={() => setFilters({ ...filters, isRead: '1' })}
              className={`px-4 py-2 rounded-lg transition-colors ${
                filters.isRead === '1'
                  ? 'bg-primary-500 text-white'
                  : 'bg-white text-dark-600 hover:bg-gray-100'
              }`}
            >
              Read
            </button>
          </div>
        </div>

        {/* Contacts List */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto"></div>
            </div>
          ) : contacts.length === 0 ? (
            <div className="p-12 text-center">
              <FaEnvelope className="w-12 h-12 text-dark-300 mx-auto mb-4" />
              <p className="text-dark-500">No messages found</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {contacts.map((contact) => (
                <div
                  key={contact.id}
                  className={`p-4 sm:p-6 hover:bg-gray-50 transition-colors cursor-pointer ${
                    !contact.is_read ? 'bg-blue-50' : ''
                  }`}
                  onClick={() => openViewModal(contact)}
                >
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className={`font-medium truncate ${
                          !contact.is_read ? 'text-dark-800' : 'text-dark-600'
                        }`}>
                          {contact.name}
                        </h3>
                        {!contact.is_read && (
                          <span className="flex-shrink-0 px-2 py-0.5 bg-blue-500 text-white text-xs rounded-full">
                            New
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-dark-500 truncate">{contact.email}</p>
                      {contact.subject && (
                        <p className="text-sm font-medium text-dark-700 mt-1 truncate">
                          {contact.subject}
                        </p>
                      )}
                      <p className="text-dark-600 mt-2 line-clamp-2">{contact.message}</p>
                      <p className="text-xs text-dark-400 mt-2">{formatDate(contact.created_at)}</p>
                    </div>
                    <div className="flex items-center space-x-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => openViewModal(contact)}
                        className="p-2 text-dark-400 hover:text-primary-500 transition-colors"
                        title="View"
                      >
                        <FaEye />
                      </button>
                      <button
                        onClick={() => handleMarkRead(contact.id, !contact.is_read)}
                        className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                          contact.is_read
                            ? 'bg-gray-100 text-dark-600 hover:bg-gray-200'
                            : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                        }`}
                      >
                        {contact.is_read ? 'Mark Unread' : 'Mark Read'}
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(contact.id)}
                        className="p-2 text-dark-400 hover:text-red-500 transition-colors"
                        title="Delete"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-center space-x-2">
            <button
              onClick={() => setPagination(p => ({ ...p, page: Math.max(1, p.page - 1) }))}
              disabled={pagination.page === 1}
              className="px-4 py-2 border border-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Previous
            </button>
            <span className="text-dark-600">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <button
              onClick={() => setPagination(p => ({ ...p, page: Math.min(p.totalPages, p.page + 1) }))}
              disabled={pagination.page === pagination.totalPages}
              className="px-4 py-2 border border-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* View Contact Modal */}
      {viewContact && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            className="bg-white rounded-xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-dark-800">Message Details</h3>
              <button
                onClick={() => setViewContact(null)}
                className="p-2 text-dark-400 hover:text-dark-600 transition-colors"
              >
                <FaTimes />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm text-dark-500">From</label>
                <p className="font-medium text-dark-800">{viewContact.name}</p>
              </div>

              <div>
                <label className="text-sm text-dark-500">Email</label>
                <p className="text-dark-700">
                  <a href={`mailto:${viewContact.email}`} className="text-primary-500 hover:underline">
                    {viewContact.email}
                  </a>
                </p>
              </div>

              {viewContact.phone && (
                <div>
                  <label className="text-sm text-dark-500">Phone</label>
                  <p className="text-dark-700 flex items-center">
                    <FaPhone className="mr-2 text-dark-400" />
                    <a href={`tel:${viewContact.phone}`} className="text-primary-500 hover:underline">
                      {viewContact.phone}
                    </a>
                  </p>
                </div>
              )}

              {viewContact.subject && (
                <div>
                  <label className="text-sm text-dark-500">Subject</label>
                  <p className="font-medium text-dark-800">{viewContact.subject}</p>
                </div>
              )}

              <div>
                <label className="text-sm text-dark-500">Message</label>
                <div className="mt-1 p-4 bg-gray-50 rounded-lg">
                  <p className="text-dark-700 whitespace-pre-wrap">{viewContact.message}</p>
                </div>
              </div>

              <div>
                <label className="text-sm text-dark-500">Received</label>
                <p className="text-dark-700">{formatDate(viewContact.created_at)}</p>
              </div>
            </div>

            <div className="flex justify-between mt-6 pt-4 border-t">
              <button
                onClick={() => handleMarkRead(viewContact.id, !viewContact.is_read)}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  viewContact.is_read
                    ? 'bg-gray-100 text-dark-600 hover:bg-gray-200'
                    : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                }`}
              >
                {viewContact.is_read ? 'Mark Unread' : 'Mark Read'}
              </button>
              <div className="flex space-x-2">
                <a
                  href={`mailto:${viewContact.email}?subject=Re: ${viewContact.subject || 'Your Message'}`}
                  className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                >
                  Reply
                </a>
                <button
                  onClick={() => { setDeleteConfirm(viewContact.id); }}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <motion.div
            className="bg-white rounded-xl p-6 max-w-md w-full"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <h3 className="text-lg font-bold text-dark-800 mb-4">Delete Message?</h3>
            <p className="text-dark-500 mb-6">
              Are you sure you want to delete this message? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-dark-600 hover:text-dark-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                Delete
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AdminLayout>
  );
};

export default ContactsManagement;
